/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { useValidImage } from '@hooks/useValidImage';
import {
	Box,
	Flex,
	NumberFormatter,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { getCurrencyUnit } from '@utils/currency-units';
import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import logoArec from '@assets/svg/arec.svg';
import logoInuk from '@assets/svg/inuk-blue.svg';

import { Text, Title } from '@components/v2/ui';
import { NumberInput } from '@components/v2/ui/NumberInput';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import Decimal from 'decimal.js';
import classes from './AdditionalityPoolProjectCard.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

export type TProjectCardProps = {
	title: string;
	cover: string;
	description: string;
	financing: {
		unlimited: boolean;
		pledged_euro: number;
		target_euro?: number;
	};
	onLearnMore: MouseEventHandler<HTMLDivElement>;
	fullWidth: boolean;
	isAdded: boolean;
	disabled: boolean;
	disableNumberInput: boolean;
	remainingQuantity: number;
	price: number;
	onChangePrice?: (value: number) => void;
	isArec?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AdditionalityPoolProjectCard: React.FC<
	TProjectCardProps
> = ({
	title,
	description,
	fullWidth,
	cover,
	financing,
	onLearnMore,
	isAdded,
	disabled,
	remainingQuantity,
	disableNumberInput,
	price,
	onChangePrice,
	isArec,
}) => {
	const { t } = useTranslation();

	const { ref: refCover, width: widthCover } =
		useElementSize();

	const coverUrl = useValidImage(cover);

	const { checkRole } = useAuthorization();
	/* ******************************** RENDERING ******************************* */
	return (
		<Box
			ref={refCover}
			onClick={onLearnMore}
			maw={fullWidth ? undefined : '380px'}
			p={'var(--mantine-spacing-md)'}
			className={classes.container}
			style={{
				width: '100%',
				borderRadius: '24px',
				position: 'relative',
				background: `radial-gradient(
                        circle at 0% 0%, 
                        rgba(255, 255, 255, 0.7) 0%, 
                        rgba(255, 255, 255, 0.7) 30%, 
                        rgba(255, 255, 255, 0) 75%
                    ),
		lightgray url(${coverUrl}) right bottom / cover no-repeat
	`,
			}}
		>
			<Flex
				direction={'column'}
				justify={'flex-start'}
				align={'flex-start'}
			>
				<img
					src={isArec ? logoArec : logoInuk}
					alt="Logo-inuk"
					width={140}
				/>
				<Title
					mt={'16px'}
					mb={'24px'}
					maw={'190px'}
					order={6}
					c={'var(--mantine-color-igluPrimary-9)'}
					style={{
						textShadow:
							'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
					}}
				>
					{isArec
						? t('global.entrustProjectSelectionArec')
						: t('global.entrustProjectSelectionInuk')}
				</Title>

				{!checkRole([ROLES.STAFF]) && (
					<Text
						variant={'subtitle1'}
						c={'var(--mantine-color-igluPrimary-9)'}
					>
						{t('global.financialNeeds')}
						{' : '}
						<Text
							span
							variant="subtitle1"
							c="var(--mantine-color-igluPrimary-9)"
							style={{
								textShadow:
									'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
							}}
						>
							{financing.unlimited ? (
								t('global.unlimited')
							) : (
								<NumberFormatter
									thousandSeparator=" "
									value={financing?.target_euro ?? 0}
									suffix={' ' + getCurrencyUnit('EUR')}
									decimalScale={2}
								/>
							)}
						</Text>
					</Text>
				)}
				{!checkRole([ROLES.STAFF]) && (
					<Text
						variant={'subtitle1'}
						c={'var(--mantine-color-igluPrimary-9)'}
						style={{
							textShadow:
								'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
						}}
					>
						{t('global.fundedPart')}
						{' : '}
						<Text
							span
							variant={'subtitle1'}
							c={'var(--mantine-color-igluPrimary-9)'}
							style={{
								textShadow:
									'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
							}}
						>
							<NumberFormatter
								thousandSeparator=" "
								value={financing.pledged_euro}
								suffix={' ' + getCurrencyUnit('EUR')}
								decimalScale={2}
							/>
						</Text>
					</Text>
				)}
				<NumberInput
					unit="€"
					min={0}
					clampBehavior="strict"
					label={`${t('global.allocationAmountIn')} €`}
					mt="24px"
					size="md"
					placeholder="0"
					disabled={disableNumberInput}
					value={price}
					defaultValue={0}
					radius={'xl'}
					onChange={!disabled ? onChangePrice : () => {}}
					styles={{
						input: {
							border: isAdded
								? '1.5px solid var(--Colors-Blue, #007AFF)'
								: '',
							backgroundColor: disabled
								? '#F0F0F0'
								: undefined,
							color: disabled ? '#6C6C6C' : undefined,
							cursor: disabled ? 'not-allowed' : 'text',
						},
					}}
				/>

				{price > 0 && (
					<Flex justify="center" mt="8px">
						<Text
							style={{
								textShadow:
									'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
							}}
							variant="subtitle1"
							c={'var(--mantine-color-igluPrimary-9)'}
						>
							{`${t('global.remainingToAllocate')} : `}
							<Text
								style={{
									textShadow:
										'0 1px 0 rgba(255,255,255,0.95),0 3px 8px rgba(255,255,255,0.6),0 6px 18px rgba(255,255,255,0.35)',
								}}
								span
								variant="subtitle1"
								c={disabled ? '#6C6C6C' : '#007AFF'}
							>
								{`${new Decimal(remainingQuantity).toDecimalPlaces(2).toString()} €`}
							</Text>
						</Text>
					</Flex>
				)}
			</Flex>
		</Box>
	);
};
